import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

export default function TypesFichiers() {
  const [types, setTypes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId]   = useState(null);
  const [editVal, setEditVal] = useState({ nom: '', code: '' });
  const [newRow, setNewRow]   = useState(false);
  const [newVal, setNewVal]   = useState({ nom: '', code: '' });

  const load = async () => {
    setLoading(true);
    try { const r = await api.getUploadTypes(); setTypes(r.data || []); }
    catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newVal.nom.trim()) return toast.error('Nom requis');
    try {
      await api.createUploadType({ nom: newVal.nom.trim(), code: newVal.code.trim() || newVal.nom.trim().toUpperCase().replace(/\s+/g, '_') });
      toast.success('Type créé');
      setNewRow(false); setNewVal({ nom: '', code: '' });
      load();
    } catch { toast.error('Erreur création'); }
  };

  const handleUpdate = async (type_id) => {
    if (!editVal.nom.trim()) return toast.error('Nom requis');
    try {
      await api.updateUploadType(type_id, { nom: editVal.nom.trim(), code: editVal.code.trim() });
      toast.success('Mis à jour');
      setEditId(null);
      load();
    } catch { toast.error('Erreur mise à jour'); }
  };

  const handleDelete = async (type_id, nom) => {
    if (!window.confirm(`Supprimer le type "${nom}" ?`)) return;
    try {
      await api.deleteUploadType(type_id);
      toast.success('Supprimé');
      load();
    } catch { toast.error('Erreur suppression'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>Types de fichiers</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Catégories utilisées pour classer les documents uploadés (Bon de commande, Contrat signé, etc.)
          </p>
        </div>
        <button
          onClick={() => { setNewRow(true); setNewVal({ nom: '', code: '' }); }}
          className="flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg"
          style={{ background: 'var(--primary-solid)', color: '#fff', border: 'none', cursor: 'pointer' }}
        >
          <Plus size={15} /> Nouveau type
        </button>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
              <th className="text-left px-4 py-3 text-xs font-semibold" style={{ color: 'var(--text-muted)', width: '45%' }}>Nom</th>
              <th className="text-left px-4 py-3 text-xs font-semibold" style={{ color: 'var(--text-muted)', width: '40%' }}>Code</th>
              <th className="text-right px-4 py-3 text-xs font-semibold" style={{ color: 'var(--text-muted)', width: '15%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Nouvelle ligne */}
            {newRow && (
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
                <td className="px-4 py-2">
                  <input
                    autoFocus
                    value={newVal.nom}
                    onChange={e => setNewVal(p => ({ ...p, nom: e.target.value, code: e.target.value.toUpperCase().replace(/\s+/g, '_') }))}
                    placeholder="Ex : Bon de commande"
                    className="w-full text-sm px-2 py-1 rounded"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text)' }}
                    onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setNewRow(false); }}
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    value={newVal.code}
                    onChange={e => setNewVal(p => ({ ...p, code: e.target.value }))}
                    placeholder="Ex : BON_COMMANDE"
                    className="w-full text-sm px-2 py-1 rounded font-mono"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text)' }}
                    onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setNewRow(false); }}
                  />
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={handleCreate} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981' }}><Check size={16} /></button>
                    <button onClick={() => setNewRow(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><X size={16} /></button>
                  </div>
                </td>
              </tr>
            )}

            {loading && (
              <tr><td colSpan={3} className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>Chargement...</td></tr>
            )}
            {!loading && types.length === 0 && !newRow && (
              <tr><td colSpan={3} className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>Aucun type — créez-en un ci-dessus</td></tr>
            )}

            {types.map((t, i) => (
              <tr
                key={t.type_id}
                style={{ borderBottom: i < types.length - 1 ? '1px solid var(--border)' : 'none' }}
              >
                <td className="px-4 py-2.5">
                  {editId === t.type_id ? (
                    <input
                      autoFocus
                      value={editVal.nom}
                      onChange={e => setEditVal(p => ({ ...p, nom: e.target.value }))}
                      className="w-full text-sm px-2 py-1 rounded"
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text)' }}
                      onKeyDown={e => { if (e.key === 'Enter') handleUpdate(t.type_id); if (e.key === 'Escape') setEditId(null); }}
                    />
                  ) : (
                    <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{t.nom}</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  {editId === t.type_id ? (
                    <input
                      value={editVal.code}
                      onChange={e => setEditVal(p => ({ ...p, code: e.target.value }))}
                      className="w-full text-sm px-2 py-1 rounded font-mono"
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text)' }}
                      onKeyDown={e => { if (e.key === 'Enter') handleUpdate(t.type_id); if (e.key === 'Escape') setEditId(null); }}
                    />
                  ) : (
                    <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>{t.code}</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {editId === t.type_id ? (
                      <>
                        <button onClick={() => handleUpdate(t.type_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981' }}><Check size={15} /></button>
                        <button onClick={() => setEditId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><X size={15} /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setEditId(t.type_id); setEditVal({ nom: t.nom, code: t.code }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><Pencil size={14} /></button>
                        <button onClick={() => handleDelete(t.type_id, t.nom)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
